import { clientMoveIn } from "../../lib/clientMoveIn";
import { fetchWithAuth } from "../../lib/api";
import type { PropertyInvoiceHistory } from "../properties/PropertyHistoryPanels";
// src/components/clients/SendInvoiceModal.tsx

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  Chip,
  IconButton,
  Paper,
  Grid,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Preview as PreviewIcon,
  Calculate as CalculateIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import type { Client, Agent } from "./types";
import type {
  InvoiceProperty,
  PropertyContact,
  InvoiceFormState,
  InvoiceFormErrors,
  CreateInvoiceRequest,
  InvoiceDeliveryState,
} from "./invoiceTypes";
import { INVOICE_CONSTANTS } from "./invoiceTypes";
import {
  fetchPropertiesForInvoice,
  previewInvoice,
  createAndSendInvoice,
  calculateCommissionAmount,
  calculateDueDate,
  formatDateForInput,
} from "../../services/invoiceService";
import { emailService } from "../../services/emailService";

// Extended form state for multi-select
interface ExtendedInvoiceFormState
  extends Omit<
    InvoiceFormState,
    "recipientContactId" | "recipientName" | "recipientEmail"
  > {
  selectedContacts: PropertyContact[];
  customEmail: string;
  includeCustomEmail: boolean;
}

interface SendInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  client: Client;
  onSuccess?: () => void;
  currentAgent?: Agent | null;
}

const SendInvoiceModal: React.FC<SendInvoiceModalProps> = ({
  open,
  onClose,
  client,
  onSuccess,
  currentAgent,
}) => {
  // Properties data
  const [properties, setProperties] = useState<InvoiceProperty[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  const commissionEditVersion = useRef(0);

  // Selected property data
  const [selectedProperty, setSelectedProperty] =
    useState<InvoiceProperty | null>(null);

  // Form state with multi-select support
  const [formState, setFormState] = useState<ExtendedInvoiceFormState>({
    propertyId: "",
    commissionBasis: "percentage",
    selectedContacts: [],
    customEmail: "",
    includeCustomEmail: false,
    billToName: "",
    propertyName: "",
    propertyAddress: "",
    baseRent: "",
    commissionPercentage:
      INVOICE_CONSTANTS.defaultCommissionPercentage.toString(),
    amount: "",
    invoiceDate: formatDateForInput(new Date()),
    dueDate: calculateDueDate(formatDateForInput(new Date())),
    moveInDate: clientMoveIn(client) || "",
  });

  // UI state
  const [errors, setErrors] = useState<
    InvoiceFormErrors & { selectedContacts?: string }
  >({});
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [previewPdf, setPreviewPdf] = useState<string | null>(null);
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState<
    string | null
  >(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [agentName, setAgentName] = useState<string>("");

  // PKT-0483: durable send-action key + reconciliation truth. The key is
  // minted once per send intent and retained across double-click, timeout,
  // retry, and reload (sessionStorage, opaque UUID only — never recipient or
  // invoice content). On delivery_unknown / reconciliation states the
  // ordinary Send control is hidden: a resend can duplicate a real invoice.
  const sendActionStorageKey = `invoiceSendAction:${client.id}`;
  const [reconciliationState, setReconciliationState] = useState<
    InvoiceDeliveryState | null
  >(null);

  const mintUuid = (): string => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const obtainSendActionId = (): string => {
    try {
      const existing = sessionStorage.getItem(sendActionStorageKey);
      if (existing) return existing;
      const minted = mintUuid();
      sessionStorage.setItem(sendActionStorageKey, minted);
      return minted;
    } catch {
      return mintUuid();
    }
  };

  const clearSendActionId = () => {
    try {
      sessionStorage.removeItem(sendActionStorageKey);
    } catch {
      // storage unavailable — nothing persisted to clear
    }
  };

  // Computed: All recipient emails
  const allRecipientEmails = useMemo(() => {
    const emails: string[] = formState.selectedContacts.map((c) => c.email);
    if (formState.includeCustomEmail && formState.customEmail.trim()) {
      emails.push(formState.customEmail.trim());
    }
    return emails;
  }, [
    formState.selectedContacts,
    formState.customEmail,
    formState.includeCustomEmail,
  ]);

  // Computed: Primary recipient (first selected)
  const primaryRecipient = useMemo(() => {
    if (formState.selectedContacts.length > 0) {
      return formState.selectedContacts[0];
    }
    if (formState.includeCustomEmail && formState.customEmail.trim()) {
      return { name: "Custom", email: formState.customEmail.trim() };
    }
    return null;
  }, [
    formState.selectedContacts,
    formState.customEmail,
    formState.includeCustomEmail,
  ]);

  // Load properties on mount
  useEffect(() => {
    if (open) {
      loadProperties();
      loadAgentInfo();
      resetForm();
    }
  }, [open]);

  // Load agent info
  const loadAgentInfo = async () => {
    try {
      if (currentAgent?.full_name) {
        setAgentName(currentAgent.full_name);
        return;
      }

      // Unassigned clients do not have a currentAgent. The fallback must use
      // the identity-fenced singleton; the ownerless legacy localStorage key is
      // deliberately retired by C0 and must never become invoice authority.
      const scopedAgentName = emailService.getConfig().agentName.trim();
      if (scopedAgentName) setAgentName(scopedAgentName);
    } catch (error) {
      console.error("Error loading agent info:", error);
    }
  };

  // Load properties
  const loadProperties = async () => {
    setLoadingProperties(true);
    try {
      const data = await fetchPropertiesForInvoice();
      setProperties(data);
    } catch (error) {
      console.error("Error loading properties:", error);
    } finally {
      setLoadingProperties(false);
    }
  };

  // Reset form
  const resetForm = () => {
    commissionEditVersion.current += 1;
    setFormState({
      propertyId: "",
      commissionBasis: "percentage",
      selectedContacts: [],
      customEmail: "",
      includeCustomEmail: false,
      billToName: "",
      propertyName: "",
      propertyAddress: "",
      baseRent: "",
      commissionPercentage:
        INVOICE_CONSTANTS.defaultCommissionPercentage.toString(),
      amount: "",
      invoiceDate: formatDateForInput(new Date()),
      dueDate: calculateDueDate(formatDateForInput(new Date())),
      moveInDate: clientMoveIn(client) || "",
    });
    setSelectedProperty(null);
    setErrors({});
    setPreviewPdf(null);
    setPreviewInvoiceNumber(null);
    setSuccessMessage(null);
  };

  // Handle property selection
  const handlePropertyChange = (property: InvoiceProperty | null) => {
    setSelectedProperty(property);

    const commissionVersion = ++commissionEditVersion.current;
    if (property) {
      void fetchWithAuth<PropertyInvoiceHistory>(`/properties-management/${property.id}/invoice-history`).then(history => {
        const learned=history.learned_commission;
        if (!learned || commissionEditVersion.current !== commissionVersion) return;
        setFormState(prev => prev.propertyId !== property.id ? prev : ({...prev,
          commissionBasis: learned.basis,
          commissionPercentage: learned.basis === 'percentage' ? String(learned.value) : '0',
          amount: learned.basis === 'flat' ? String(learned.value) : (Number(prev.baseRent)*Number(learned.value)/100).toFixed(2),
        }));
        setPreviewPdf(null);
        setPreviewInvoiceNumber(null);
      }).catch(() => {
        if (commissionEditVersion.current === commissionVersion) {
          setErrors(prev=>({...prev,general:'Could not load the previous commission. Verify the terms before sending.'}));
        }
      });
      // Auto-select first contact if available
      const initialContacts =
        property.contacts.length > 0 ? [property.contacts[0]] : [];

      setFormState((prev) => ({
        ...prev,
        propertyId: property.id,
        // Learned terms belong to one building. Reset them before its new read,
        // so an absent or failed history cannot reuse another property's flat fee.
        commissionBasis: "percentage",
        commissionPercentage: String(INVOICE_CONSTANTS.defaultCommissionPercentage),
        amount: Number(prev.baseRent) > 0
          ? (Number(prev.baseRent) * INVOICE_CONSTANTS.defaultCommissionPercentage / 100).toFixed(2)
          : "",
        propertyName: property.name,
        propertyAddress: property.address,
        billToName: property.property_management || "",
        selectedContacts: initialContacts,
        customEmail: "",
        includeCustomEmail: false,
      }));
    } else {
      setFormState((prev) => ({
        ...prev,
        propertyId: "",
        commissionBasis: "percentage",
        propertyName: "",
        propertyAddress: "",
        billToName: "",
        selectedContacts: [],
        customEmail: "",
        includeCustomEmail: false,
      }));
    }

    setPreviewPdf(null);
    setPreviewInvoiceNumber(null);
  };

  // Handle multi-select contact change
  const handleContactsChange = (_event: any, newValue: PropertyContact[]) => {
    setFormState((prev) => ({
      ...prev,
      selectedContacts: newValue,
    }));
    setPreviewPdf(null);
  };

  // Handle custom email toggle
  const handleCustomEmailToggle = (checked: boolean) => {
    setFormState((prev) => ({
      ...prev,
      includeCustomEmail: checked,
    }));
    setPreviewPdf(null);
  };

  // Handle base rent change and auto-calculate amount
  const handleBaseRentChange = (value: string) => {
    commissionEditVersion.current += 1;
    const numericValue = value.replace(/[^0-9.]/g, "");
    const baseRent = parseFloat(numericValue) || 0;
    const percentage = parseFloat(formState.commissionPercentage) || 0;
    const calculatedAmount = calculateCommissionAmount(baseRent, percentage);

    setFormState((prev) => ({
      ...prev,
      baseRent: numericValue,
      amount: prev.commissionBasis === "flat" ? prev.amount : calculatedAmount > 0 ? calculatedAmount.toFixed(2) : "",
    }));

    setPreviewPdf(null);
  };

  // Handle commission percentage change
  const handleCommissionChange = (value: string) => {
    commissionEditVersion.current += 1;
    const numericValue = value.replace(/[^0-9.]/g, "");
    const percentage = Math.min(parseFloat(numericValue) || 0, 100);
    const baseRent = parseFloat(formState.baseRent) || 0;
    const calculatedAmount = calculateCommissionAmount(baseRent, percentage);

    setFormState((prev) => ({
      ...prev,
      commissionPercentage: percentage.toString(),
      amount: calculatedAmount > 0 ? calculatedAmount.toFixed(2) : "",
    }));

    setPreviewPdf(null);
  };

  // Handle invoice date change and recalculate due date
  const handleInvoiceDateChange = (value: string) => {
    setFormState((prev) => ({
      ...prev,
      invoiceDate: value,
      dueDate: calculateDueDate(value),
    }));
    setPreviewPdf(null);
  };

  // Handle generic field change
  const handleFieldChange = (
    field: keyof ExtendedInvoiceFormState,
    value: string | boolean
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPreviewPdf(null);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: InvoiceFormErrors & { selectedContacts?: string } = {};

    if (!formState.propertyId) {
      newErrors.propertyId = "Please select a property";
    }

    // Validate recipients
    if (allRecipientEmails.length === 0) {
      newErrors.selectedContacts = "Please select at least one recipient";
    } else {
      // Validate email formats
      const invalidEmails = allRecipientEmails.filter(
        (email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      );
      if (invalidEmails.length > 0) {
        newErrors.selectedContacts = `Invalid email format: ${invalidEmails.join(
          ", "
        )}`;
      }
    }

    if (formState.includeCustomEmail && formState.customEmail.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.customEmail.trim())) {
        newErrors.recipientEmail = "Invalid custom email format";
      }
    }

    if (!formState.baseRent || parseFloat(formState.baseRent) <= 0) {
      newErrors.baseRent = "Base rent must be greater than 0";
    }
    if (!formState.amount || parseFloat(formState.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }
    if (!formState.moveInDate) {
      newErrors.moveInDate = "Move-in date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Build request data
  const buildRequestData = (): CreateInvoiceRequest => {
    return {
      clientId: client.id,
      propertyId: formState.propertyId,
      baseRent: parseFloat(formState.baseRent),
      commissionPercentage: parseFloat(formState.commissionPercentage),
      commissionBasis: formState.commissionBasis || "percentage",
      amount: parseFloat(formState.amount),
      recipientName: primaryRecipient?.name || "Property Management",
      recipientEmail: primaryRecipient?.email || "",
      // Include all recipient emails for CC functionality
      recipientEmails: allRecipientEmails,
      billToName: formState.billToName || "Property Management",
      propertyName: formState.propertyName,
      propertyAddress: formState.propertyAddress,
      clientName: `${client.first_name} ${client.last_name}`,
      moveInDate: formState.moveInDate,
      invoiceDate: formState.invoiceDate,
      dueDate: formState.dueDate,
      agentName: agentName,
    };
  };

  // Handle preview
  const handlePreview = async () => {
    if (!validateForm()) return;

    setIsPreviewLoading(true);
    setErrors({});

    try {
      const requestData = buildRequestData();
      const result = await previewInvoice(requestData);

      if (result.success && result.pdfBase64) {
        setPreviewPdf(result.pdfBase64);
        setPreviewInvoiceNumber(result.invoiceNumber || null);
      } else {
        setErrors({ general: result.error || "Failed to generate preview" });
      }
    } catch (error: any) {
      setErrors({ general: error.message || "Failed to generate preview" });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Handle send
  const handleSend = async () => {
    if (!validateForm()) return;

    setIsSendLoading(true);
    setErrors({});

    try {
      // PKT-0483: the SAME action key across double-click/timeout/retry —
      // the backend returns existing delivery truth instead of resending.
      const requestData = {
        ...buildRequestData(),
        sendActionId: obtainSendActionId(),
      };
      const result = await createAndSendInvoice(requestData);

      // PKT-0483 FIX-1: branch on DELIVERY STATE, never on `success` — the
      // route legitimately returns success=true with
      // accepted_reconciliation_required (durable receipt, incomplete local
      // records). Guarding on !success made this state invisible: plain
      // success shown, key discarded, Send re-exposed (the reviewed defect).
      if (
        result.deliveryState === "delivery_unknown" ||
        result.deliveryState === "legacy_delivery_unknown" ||
        result.deliveryState === "accepted_reconciliation_required"
      ) {
        // Delivery truth is unresolved: Gmail may have (or has) accepted the
        // invoice. Hide ordinary Send — a resend can bill the client twice —
        // and RETAIN the persisted action key: it is the operator's handle on
        // the unresolved action.
        setReconciliationState(result.deliveryState);
        setErrors({
          general:
            result.error ||
            (result.deliveryState === "accepted_reconciliation_required"
              ? "Gmail accepted this invoice, but its delivery record still " +
                "needs reconciliation. The invoice was sent ONCE — do not " +
                "resend."
              : "Delivery could not be confirmed for this invoice. It was " +
                "NOT sent again. Do not resend until delivery is reconciled."),
        });
        return;
      }

      if (result.success) {
        clearSendActionId();
        const invoiceNumber =
          result.invoice?.invoice_number || previewInvoiceNumber || "Unknown";

        // PKT-0496: build the message from the SUBMITTED envelope counts the
        // backend returns — never from our original requested-recipient
        // array, which may include a CC the delivery policy omitted. A
        // provider receipt proves submission, not recipient delivery.
        const submittedCount = result.submittedRecipientCount;
        const blockedCcCount = result.blockedCcCount ?? 0;
        const submittedText =
          typeof submittedCount === "number"
            ? submittedCount === 1
              ? "1 recipient"
              : `${submittedCount} recipients`
            : "the submitted recipients";
        const blockedText =
          blockedCcCount > 0
            ? ` ${blockedCcCount} CC recipient${
                blockedCcCount === 1 ? " was" : "s were"
              } omitted by your delivery block list.`
            : "";

        setSuccessMessage(
          `Invoice ${invoiceNumber} submitted to ${submittedText}.${blockedText}`
        );

        // Activity logging is handled by the backend
        // Close modal after short delay to show success message
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 2000);
      } else {
        setErrors({ general: result.error || "Failed to send invoice" });
      }
    } catch (error: any) {
      setErrors({ general: error.message || "Failed to send invoice" });
    } finally {
      setIsSendLoading(false);
    }
  };

  // Calculate amount display
  const calculatedAmount = useMemo(() => {
    if (formState.commissionBasis === "flat") return 0;
    const baseRent = parseFloat(formState.baseRent) || 0;
    const percentage = parseFloat(formState.commissionPercentage) || 0;
    return calculateCommissionAmount(baseRent, percentage);
  }, [formState.baseRent, formState.commissionPercentage, formState.commissionBasis]);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6">Send Invoice</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {successMessage ? (
          <Alert severity="success" sx={{ my: 2 }}>
            {successMessage}
          </Alert>
        ) : (
          <Stack spacing={3}>
            {errors.general && <Alert severity="error">{errors.general}</Alert>}

            {/* Client Info (Read-only) */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover" }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                <PersonIcon
                  sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
                />
                Client
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {client.first_name} {client.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {client.email}
              </Typography>
            </Paper>

            {/* Property Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                <HomeIcon
                  sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
                />
                Property *
              </Typography>
              <Autocomplete
                options={properties}
                getOptionLabel={(option) => option.name}
                value={selectedProperty}
                onChange={(_, value) => handlePropertyChange(value)}
                loading={loadingProperties}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select property"
                    error={!!errors.propertyId}
                    helperText={errors.propertyId}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingProperties && <CircularProgress size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.property_management || "No management company"}{" "}
                        • {option.contacts.length} contact(s)
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Box>

            {/* Property Details (when selected) */}
            {selectedProperty && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Property Name"
                      value={formState.propertyName}
                      onChange={(e) =>
                        handleFieldChange("propertyName", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Bill To (Management Company)"
                      value={formState.billToName}
                      onChange={(e) =>
                        handleFieldChange("billToName", e.target.value)
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Property Address"
                      value={formState.propertyAddress}
                      onChange={(e) =>
                        handleFieldChange("propertyAddress", e.target.value)
                      }
                    />
                  </Grid>
                </Grid>

                {/* Multi-Select Recipient Contacts */}
                <Divider />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    <EmailIcon
                      sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
                    />
                    Send Invoice To *
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    Select one or more contacts to receive the invoice
                  </Typography>

                  {selectedProperty.contacts.length > 0 ? (
                    <Stack spacing={2}>
                      {/* Multi-select Autocomplete for contacts */}
                      <Autocomplete
                        multiple
                        options={selectedProperty.contacts}
                        disableCloseOnSelect
                        getOptionLabel={(option) =>
                          `${option.name} (${option.email})`
                        }
                        value={formState.selectedContacts}
                        onChange={handleContactsChange}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            <Box>
                              <Typography variant="body2">
                                {option.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {option.email}
                                {option.role && ` • ${option.role}`}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option.id}
                              label={option.name}
                              size="small"
                              avatar={
                                <Box
                                  component="span"
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    bgcolor: "primary.main",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  {option.name.charAt(0).toUpperCase()}
                                </Box>
                              }
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              formState.selectedContacts.length === 0
                                ? "Select contacts..."
                                : ""
                            }
                            error={!!errors.selectedContacts}
                            helperText={errors.selectedContacts}
                          />
                        )}
                      />

                      {/* Selected contacts summary */}
                      {formState.selectedContacts.length > 0 && (
                        <Paper
                          variant="outlined"
                          sx={{ p: 1.5, bgcolor: "action.hover" }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Invoice will be sent to:
                          </Typography>
                          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                            {formState.selectedContacts.map(
                              (contact, index) => (
                                <Typography key={contact.id} variant="body2">
                                  {index === 0 ? "📧 " : "📋 "}
                                  {contact.name} &lt;{contact.email}&gt;
                                  {index === 0 &&
                                    formState.selectedContacts.length > 1 && (
                                      <Chip
                                        label="Primary"
                                        size="small"
                                        sx={{ ml: 1, height: 18 }}
                                      />
                                    )}
                                </Typography>
                              )
                            )}
                          </Stack>
                        </Paper>
                      )}

                      {/* Custom email option */}
                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formState.includeCustomEmail}
                              onChange={(e) =>
                                handleCustomEmailToggle(e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Add additional recipient email
                            </Typography>
                          }
                        />
                        {formState.includeCustomEmail && (
                          <TextField
                            fullWidth
                            size="small"
                            label="Additional Email"
                            placeholder="email@example.com"
                            value={formState.customEmail}
                            onChange={(e) =>
                              handleFieldChange("customEmail", e.target.value)
                            }
                            error={!!errors.recipientEmail}
                            helperText={errors.recipientEmail}
                            sx={{ mt: 1 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AddIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Box>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <Alert severity="warning">
                        No contacts found for this property. Please enter a
                        custom email.
                      </Alert>
                      <TextField
                        fullWidth
                        label="Recipient Email *"
                        placeholder="email@example.com"
                        value={formState.customEmail}
                        onChange={(e) => {
                          handleFieldChange("customEmail", e.target.value);
                          handleFieldChange("includeCustomEmail", true);
                        }}
                        error={
                          !!errors.selectedContacts || !!errors.recipientEmail
                        }
                        helperText={
                          errors.selectedContacts || errors.recipientEmail
                        }
                      />
                    </Stack>
                  )}
                </Box>
              </>
            )}

            <TextField select label="Commission basis" value={formState.commissionBasis || 'percentage'} onChange={e=>{commissionEditVersion.current += 1;setFormState(prev=>({...prev,commissionBasis:e.target.value as 'flat'|'percentage'}));}}>
              <MenuItem value="percentage">Percentage of first month's rent</MenuItem><MenuItem value="flat">Flat fee</MenuItem>
            </TextField>
            {/* Financial Details */}
            <Divider />
            <Typography variant="subtitle2" gutterBottom>
              <CalculateIcon
                sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }}
              />
              Invoice Details
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Base Rent *"
                  value={formState.baseRent}
                  onChange={(e) => handleBaseRentChange(e.target.value)}
                  error={!!errors.baseRent}
                  helperText={errors.baseRent}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  placeholder="0.00"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  disabled={formState.commissionBasis === "flat"}
                  label="Commission %"
                  value={formState.commissionPercentage}
                  onChange={(e) => handleCommissionChange(e.target.value)}
                  error={!!errors.commissionPercentage}
                  helperText={errors.commissionPercentage}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Invoice Amount *"
                  value={formState.amount}
                  onChange={(e) => { commissionEditVersion.current += 1; handleFieldChange("amount", e.target.value.replace(/[^0-9.]/g, "")); }}
                  error={!!errors.amount}
                  helperText={
                    errors.amount ||
                    (calculatedAmount > 0
                      ? `Calculated: $${calculatedAmount.toFixed(2)}`
                      : "")
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  placeholder="0.00"
                />
              </Grid>
            </Grid>

            {/* Dates */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Move-In Date *"
                  value={formState.moveInDate}
                  onChange={(e) =>
                    handleFieldChange("moveInDate", e.target.value)
                  }
                  error={!!errors.moveInDate}
                  helperText={errors.moveInDate || (!client.move_in_date && client.move_in_timeline && !/^\d{4}-\d{2}-\d{2}$/.test(client.move_in_timeline) ? client.move_in_timeline : undefined)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Invoice Date"
                  value={formState.invoiceDate}
                  onChange={(e) => handleInvoiceDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Due Date (Net 30)"
                  value={formState.dueDate}
                  onChange={(e) => handleFieldChange("dueDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Auto-calculated from invoice date"
                />
              </Grid>
            </Grid>

            {/* Preview Section */}
            {previewPdf && (
              <>
                <Divider />
                <Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <Typography variant="subtitle2">
                      PDF Preview
                      {previewInvoiceNumber && (
                        <Chip
                          label={previewInvoiceNumber}
                          size="small"
                          color="primary"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={handlePreview}
                      disabled={isPreviewLoading}
                    >
                      Regenerate
                    </Button>
                  </Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      height: 400,
                      overflow: "hidden",
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    <iframe
                      src={`data:application/pdf;base64,${previewPdf}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                      }}
                      title="Invoice Preview"
                    />
                  </Paper>
                </Box>
              </>
            )}
          </Stack>
        )}
      </DialogContent>

      {!successMessage && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            disabled={isPreviewLoading || isSendLoading}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={
              isPreviewLoading ? (
                <CircularProgress size={20} />
              ) : (
                <PreviewIcon />
              )
            }
            onClick={handlePreview}
            disabled={
              isPreviewLoading || isSendLoading || !formState.propertyId
            }
          >
            {isPreviewLoading ? "Generating..." : "Preview PDF"}
          </Button>
          {/* PKT-0483: every non-new delivery state hides ordinary Send —
              delivery_unknown / reconciliation can mean Gmail already
              accepted this invoice, and a resend bills the client twice. */}
          {reconciliationState === null && (
            <Button
              variant="contained"
              startIcon={
                isSendLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              onClick={handleSend}
              disabled={isPreviewLoading || isSendLoading || !previewPdf}
              color="primary"
            >
              {isSendLoading
                ? "Sending..."
                : `Send Invoice${
                    allRecipientEmails.length > 1
                      ? ` (${allRecipientEmails.length})`
                      : ""
                  }`}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SendInvoiceModal;
